import React, { useState, useEffect, useMemo } from 'react';
import { Player, Tournament } from '../types';
import { apiPost, apiDelete, apiPut, apiGet } from '../utils/api';

interface BulkPlayerRegistrationFormProps {
  tournament: Tournament;
  players: Player[];
  onClose: () => void;
  onSuccess: () => void;
}

const BulkPlayerRegistrationForm: React.FC<BulkPlayerRegistrationFormProps> = ({
  tournament: initialTournament,
  players,
  onClose,
  onSuccess
}) => {
  // Используем локальное состояние для tournament, чтобы обновлять его при изменении пропсов
  const [tournament, setTournament] = useState(initialTournament);
  
  // Обновляем локальное состояние при изменении initialTournament
  useEffect(() => {
    setTournament(initialTournament);
  }, [initialTournament]);
  
  const isDoubles = tournament.format === 'DOUBLES';
  const isActiveMembership = (membership: any): boolean =>
    membership?.status === 'REGISTERED' || membership?.status === 'CONFIRMED';
  const isActiveMembershipForLevel = (membership: any, level: string): boolean =>
    isActiveMembership(membership) && membership?.level === level;
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [showAllLowerLevels, setShowAllLowerLevels] = useState<boolean>(false);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [selectedPairs, setSelectedPairs] = useState<Array<{player1Id: string; player2Id: string}>>([]); // Для парных турниров
  const [registeredSoloPlayers, setRegisteredSoloPlayers] = useState<string[]>([]); // Игроки без пары для парных турниров
  const [baseFee, setBaseFee] = useState<number>(0);
  const [playerSearch, setPlayerSearch] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<string>>(new Set()); // Для визуальной обратной связи
  const [wildCardPlayerIds, setWildCardPlayerIds] = useState<Set<string>>(new Set());
  const [additionalRegistrations, setAdditionalRegistrations] = useState<Map<string, number>>(new Map()); // Количество дополнительных регистраций для каждого игрока
  const additionalRegistrationsRef = React.useRef<Map<string, number>>(new Map()); // Ref для сохранения актуального значения
  const [previousLevel, setPreviousLevel] = useState<string>(''); // Для отслеживания смены уровня
  
  // Синхронизируем ref с state
  React.useEffect(() => {
    additionalRegistrationsRef.current = additionalRegistrations;
  }, [additionalRegistrations]);

  // Сравнение уровней: разрешаем регистрацию только именно на выбранный уровень (без допуска ниже/выше)
  const parseRank = (lvl?: string): { family: 'S' | 'D' | null; rank: number | null; sub: number | null; special?: string } => {
    if (!lvl) return { family: null, rank: null, sub: null };
    if (lvl.startsWith('S-')) {
      // S-4F < S-4M < S-5 < S-6 < ...
      if (lvl === 'S-4F') return { family: 'S', rank: 4, sub: 0 };
      if (lvl === 'S-4M') return { family: 'S', rank: 4, sub: 1 };
      const n = Number(lvl.split('-')[1]);
      return { family: 'S', rank: isNaN(n) ? null : n, sub: 1 };
    }
    if (lvl.startsWith('D-')) {
      const tail = lvl.split('-')[1];
      // Обрабатываем специальные случаи
      if (tail === 'Mixt') return { family: 'D', rank: 4, sub: 0, special: 'Mixt' };
      if (tail === 'ProAm') return { family: 'D', rank: 1, sub: 0, special: 'ProAm' };
      // Обрабатываем D-6.5
      if (tail === '6.5') return { family: 'D', rank: 2.5, sub: 0, special: '6.5' };
      // Обрабатываем обычные числовые уровни (D-6, D-7 и т.д.)
      const n = tail && /^\d+$/.test(tail) ? Number(tail) : null;
      return { family: 'D', rank: n, sub: 0 };
    }
    return { family: null, rank: null, sub: null };
  };

  const isEligibleForSelectedLevel = (player: Player, selected: string): boolean => {
    if (!selected) return false
    
    // ВРЕМЕННО: Для D-ProAm разрешаем всех игроков с любым уровнем в паре (как для D-7)
    // Проверяем САМЫМ ПЕРВЫМ, до всех остальных проверок
    if (selected === 'D-ProAm') {
      // Разрешаем всех игроков с любым уровнем в паре (D-ProAm, D-7, D-6.5, D-6, D-Mixt)
      return !!player.doublesLevel;
    }
    
    const sel = parseRank(selected)
    const playerLevel = sel.family === 'S' ? player.singlesLevel : player.doublesLevel;
    
    if (sel.family === 'S') {
      const pr = parseRank(player.singlesLevel || undefined)
      if (pr.family !== 'S' || pr.rank === null || sel.rank === null) return false
      if (pr.rank < sel.rank) return true
      if (pr.rank === sel.rank) return (pr.sub ?? 1) <= (sel.sub ?? 1)
      return false
    }
    if (sel.family === 'D') {
      const pr = parseRank(player.doublesLevel || undefined)
      if (pr.family !== 'D') return false
      if (selected === 'D-Mixt') {
        return playerLevel === 'D-Mixt' || playerLevel === 'D-6';
      }
      
      // Специальная обработка для уровня D-6.5: разрешаем игроков D-7 (максимум 1)
      if (selected === 'D-6.5') {
        // Для D-6.5 разрешаем: D-7, D-6.5, D-6, D-ProAm (но не D-Mixt)
        if (!playerLevel) return false;
        if (playerLevel === 'D-Mixt') return false;
        if (playerLevel === 'D-ProAm') return true;
        if (playerLevel === 'D-6.5') return true;
        if (playerLevel === 'D-7') return true; // Разрешаем D-7 для D-6.5
        if (playerLevel === 'D-6') return true;
        return false;
      }
      
      // Для остальных уровней используем стандартную логику (как для D-7)
      if (pr.rank === null || sel.rank === null) {
        // Обрабатываем специальные случаи
        return false;
      }
      return pr.rank <= sel.rank
    }
    return false
  };

  const isVisibleForSelectedLevel = (player: Player, selected: string): boolean => {
    if (!selected) return false;
    if (isEligibleForSelectedLevel(player, selected)) return true;

    if (!showAllLowerLevels || !selected.startsWith('S-')) return false;

    const sel = parseRank(selected);
    const pr = parseRank(player.singlesLevel || undefined);
    if (sel.family !== 'S' || pr.family !== 'S' || sel.rank === null || pr.rank === null) return false;

    if (pr.rank > sel.rank) return true;
    if (pr.rank === sel.rank) return (pr.sub ?? 1) > (sel.sub ?? 1);
    return false;
  };

  const levels = [
    { value: '', label: 'Выберите уровень' },
    { value: 'S-4F', label: 'S-4F (4-й женский)' },
    { value: 'S-4M', label: 'S-4M (4-й мужской)' },
    { value: 'S-5', label: 'S-5 (5-й)' },
    { value: 'S-6', label: 'S-6 (6-й)' },
    { value: 'S-7', label: 'S-7 (7-й)' },
    { value: 'S-8', label: 'S-8 (8-й)' },
    { value: 'S-9', label: 'S-9 (9-й)' },
    { value: 'D-Mixt', label: 'D-Mixt (Смешанная)' },
    { value: 'D-6', label: 'D-6 (6-й)' },
    { value: 'D-6.5', label: 'D-6.5 (6.5-й)' },
    { value: 'D-7', label: 'D-7 (7-й)' },
    { value: 'D-ProAm', label: 'D-ProAm (Про-Ам)' }
  ];


  // Фильтруем игроков по выбранному уровню и сортируем по алфавиту
  // ВРЕМЕННО: Для D-ProAm показываем ВСЕХ игроков с любым уровнем в паре (как для D-7)
  const filteredPlayers = useMemo(() => {
    const searchTokens = playerSearch
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    const matchesSearch = (name: string): boolean => {
      if (searchTokens.length === 0) return true;
      const words = name
        .toLowerCase()
        .split(/[\s\-_/.,]+/)
        .filter(Boolean);
      return searchTokens.every((token) =>
        words.some((word) => word.startsWith(token))
      );
    };

    // ВРЕМЕННО ОТКЛЮЧЕНО: Для D-ProAm показываем ВСЕХ игроков с любым уровнем
    if (selectedLevel === 'D-ProAm') {
      // Просто возвращаем всех игроков с любым уровнем в паре, БЕЗ фильтрации
      return [...players]
        .filter(player => !!player.doublesLevel) // Только игроки с указанным уровнем в паре
        .filter(player => matchesSearch(player.name))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    }
    
    if (!selectedLevel) {
      return [...players]
        .filter(player => matchesSearch(player.name))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    }
    
    return [...players]
      .filter(player => isVisibleForSelectedLevel(player, selectedLevel))
      .filter(player => matchesSearch(player.name))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [selectedLevel, players, playerSearch, showAllLowerLevels]);

  // Автоматически выбираем уже зарегистрированных игроков/пары при смене уровня
  // ВАЖНО: Сбрасываем additionalRegistrations только при смене уровня, а не при обновлении tournament.memberships
  useEffect(() => {
    // Сбрасываем счетчики только при смене уровня
    if (selectedLevel !== previousLevel) {
      setAdditionalRegistrations(new Map());
      setPreviousLevel(selectedLevel);
    }
    
    if (selectedLevel && tournament.memberships && Array.isArray(tournament.memberships)) {
      const wcIds = new Set(
        tournament.memberships
          .filter(m => isActiveMembershipForLevel(m, selectedLevel) && (m as any).isWildCard === true)
          .map(m => m.playerId)
      );
      setWildCardPlayerIds(wcIds);

      if (isDoubles) {
        // Для парных турниров: загружаем зарегистрированные пары и игроков без пары
        const teamMap = new Map<string, string[]>();
        const soloPlayerIds: string[] = [];
        
        tournament.memberships
          .filter(m => isActiveMembershipForLevel(m, selectedLevel))
          .forEach(m => {
            if (m.doublesTeamId) {
              const teamId = m.doublesTeamId;
              if (!teamMap.has(teamId)) {
                teamMap.set(teamId, []);
              }
              teamMap.get(teamId)!.push(m.playerId);
            } else {
              // Игрок без пары
              soloPlayerIds.push(m.playerId);
            }
          });
        
        const registeredPairs: Array<{player1Id: string; player2Id: string}> = [];
        // Создаем Map для хранения пар с правильным порядком
        const pairsMap = new Map<string, {player1Id: string; player2Id: string}>();
        
        // Сначала пытаемся использовать doublesTeam из memberships для сохранения порядка
        // Используем doublesTeamId как ключ для быстрого поиска
        const doublesTeamsMap = new Map<string, {player1Id: string; player2Id: string}>();
        const membershipsWithTeam = tournament.memberships.filter(m => isActiveMembershipForLevel(m, selectedLevel) && m.doublesTeamId);
        
        membershipsWithTeam.forEach(m => {
          // Проверяем, есть ли doublesTeam в membership
          const doublesTeam = (m as any).doublesTeam;
          const teamId = m.doublesTeamId!;
          // Добавляем только если еще не добавлена эта команда
          if (!doublesTeamsMap.has(teamId)) {
            if (doublesTeam && doublesTeam.id && doublesTeam.player1Id && doublesTeam.player2Id) {
              // Используем порядок из doublesTeam
              doublesTeamsMap.set(teamId, {
                player1Id: doublesTeam.player1Id,
                player2Id: doublesTeam.player2Id
              });
            }
          }
        });
        
        // Используем teamMap для получения всех пар, но сохраняем порядок из doublesTeam если доступен
        teamMap.forEach((playerIds, teamId) => {
          if (playerIds.length === 2) {
            // Полная пара - проверяем, есть ли doublesTeam с правильным порядком по teamId
            const doublesTeam = doublesTeamsMap.get(teamId);
            
            if (doublesTeam) {
              // Используем порядок из doublesTeam
              pairsMap.set(teamId, {
                player1Id: doublesTeam.player1Id,
                player2Id: doublesTeam.player2Id
              });
            } else {
              // Fallback: используем порядок из teamMap (первый добавленный игрок - первый)
              // Важно: добавляем пару даже если doublesTeam не загружен
              pairsMap.set(teamId, {
                player1Id: playerIds[0],
                player2Id: playerIds[1]
              });
            }
          } else if (playerIds.length === 1) {
            // Неполная пара (игрок без партнера) - добавляем в pairsMap с пустым player2Id
            const doublesTeam = doublesTeamsMap.get(teamId);
            
            if (doublesTeam && doublesTeam.player1Id) {
              // Используем порядок из doublesTeam, если доступен
              pairsMap.set(teamId, {
                player1Id: doublesTeam.player1Id,
                player2Id: doublesTeam.player2Id || '' // Может быть пустым, если партнер удален
              });
            } else {
              // Fallback: используем игрока из teamMap
              pairsMap.set(teamId, {
                player1Id: playerIds[0],
                player2Id: '' // Пустой ID означает отсутствие партнера
              });
            }
          } else if (playerIds.length > 2) {
            // Если в команде больше 2 игроков - это ошибка данных, но все равно показываем первых двух
            console.warn(`Команда ${teamId} имеет ${playerIds.length} игроков вместо 2:`, playerIds);
            pairsMap.set(teamId, {
              player1Id: playerIds[0],
              player2Id: playerIds[1]
            });
          }
        });
        
        registeredPairs.push(...Array.from(pairsMap.values()));
        setSelectedPairs(registeredPairs);
        setRegisteredSoloPlayers(soloPlayerIds);
      } else {
        // Для одиночных турниров: загружаем зарегистрированных игроков
        const alreadyRegisteredIds = players
          .filter(player => isEligibleForSelectedLevel(player, selectedLevel) &&
            tournament.memberships.some(
              membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
            ))
          .map(player => player.id);
        
        setSelectedPlayerIds(alreadyRegisteredIds);
      }
      
      // Устанавливаем базовую величину взносов из первого найденного участника
      const firstMembership = tournament.memberships.find(
        membership => isActiveMembershipForLevel(membership, selectedLevel)
      );
      const tournamentBaseFee = Number((tournament as any).baseFee || 0);
      const membershipFee = Number(firstMembership?.fee || 0);
      if (firstMembership && Number.isFinite(membershipFee) && membershipFee > 0) {
        setBaseFee(membershipFee);
      } else {
        // Если на уровне еще нет регистраций, используем базовый взнос турнира
        setBaseFee(tournamentBaseFee);
      }
    } else {
      setSelectedPlayerIds([]);
      setSelectedPairs([]);
      setRegisteredSoloPlayers([]);
      setWildCardPlayerIds(new Set());
      setBaseFee(Number((tournament as any).baseFee || 0));
    }
  }, [selectedLevel, tournament.memberships, (tournament as any).baseFee, isDoubles, players, previousLevel]); // Вернули tournament.memberships, но счетчики сбрасываются только при смене уровня

  // Состояние для выбора пары (для парных турниров)
  const [pairPlayer1Id, setPairPlayer1Id] = useState<string>('');
  const [pairPlayer2Id, setPairPlayer2Id] = useState<string>('');

  const handlePlayerToggle = (playerId: string) => {
    setSelectedPlayerIds(prev => 
      prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handleSelectAll = () => {
    // Получаем всех подходящих игроков (включая уже зарегистрированных)
    const allPlayersWithLevel = players.filter(player => 
      isVisibleForSelectedLevel(player, selectedLevel)
    );
    
    if (selectedPlayerIds.length === allPlayersWithLevel.length) {
      setSelectedPlayerIds([]);
    } else {
      setSelectedPlayerIds(allPlayersWithLevel.map(p => p.id));
    }
  };

  const handleWildCardToggle = (playerId: string) => {
    setWildCardPlayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  // Функция для проверки валидности пары для уровня D-ProAm
  // ВРЕМЕННО ОТКЛЮЧЕНО
  const isValidProAmPair = (player1: Player, player2: Player): { valid: boolean; error?: string } => {
    // Временное отключение контроля для D-ProAm
    return { valid: true };
    
    /* Закомментировано временно
    const p1Level = player1.doublesLevel;
    const p2Level = player2.doublesLevel;
    
    // Проверяем, что у обоих игроков указан уровень
    if (!p1Level || !p2Level) {
      return { 
        valid: false, 
        error: 'Для уровня D-ProAm у всех игроков должен быть указан уровень в паре' 
      };
    }
    
    // Подсчитываем количество игроков D-ProAm
    const proAmCount = [p1Level, p2Level].filter(level => level === 'D-ProAm').length;
    
    // В паре не может быть двух игроков D-ProAm
    if (proAmCount > 1) {
      return { 
        valid: false, 
        error: 'Для уровня D-ProAm допускается максимум 1 игрок с уровнем D-ProAm' 
      };
    }
    
    // Если один игрок D-ProAm, второй может быть любого другого уровня
    // Никаких дополнительных ограничений не требуется
    
    return { valid: true };
    */
  };

  const handleAddPair = () => {
    if (!pairPlayer1Id || !pairPlayer2Id) {
      setError('Пожалуйста, выберите обоих игроков для пары');
      return;
    }

    if (pairPlayer1Id === pairPlayer2Id) {
      setError('Игроки в паре должны быть разными');
      return;
    }

    // Получаем объекты игроков
    const player1 = players.find(p => p.id === pairPlayer1Id);
    const player2 = players.find(p => p.id === pairPlayer2Id);

    if (!player1 || !player2) {
      setError('Не удалось найти информацию об игроках');
      return;
    }

    // Для уровня D-ProAm проверяем специальные правила
    if (selectedLevel === 'D-ProAm') {
      const validation = isValidProAmPair(player1, player2);
      if (!validation.valid) {
        setError(validation.error || 'Неверная комбинация игроков для уровня D-ProAm');
        return;
      }
    }

    // Нормализуем пару для проверки дубликатов (меньший ID - player1Id)
    const normalizedPair = {
      player1Id: pairPlayer1Id < pairPlayer2Id ? pairPlayer1Id : pairPlayer2Id,
      player2Id: pairPlayer1Id < pairPlayer2Id ? pairPlayer2Id : pairPlayer1Id
    };

    // Проверяем, не добавлена ли уже эта пара (используем нормализованную для сравнения)
    const isDuplicate = selectedPairs.some(pair => {
      const normalizedExisting = {
        player1Id: pair.player1Id < pair.player2Id ? pair.player1Id : pair.player2Id,
        player2Id: pair.player1Id < pair.player2Id ? pair.player2Id : pair.player1Id
      };
      return normalizedExisting.player1Id === normalizedPair.player1Id && 
             normalizedExisting.player2Id === normalizedPair.player2Id;
    });

    if (isDuplicate) {
      setError('Эта пара уже добавлена');
      return;
    }

    // Сохраняем пару в исходном порядке выбора
    const originalPair = {
      player1Id: pairPlayer1Id,
      player2Id: pairPlayer2Id
    };

    setSelectedPairs(prev => [...prev, originalPair]);
    setPairPlayer1Id('');
    setPairPlayer2Id('');
    setError(null);
  };

  const handleRemovePair = (index: number) => {
    setSelectedPairs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedLevel) {
      setError('Пожалуйста, выберите уровень');
      return;
    }

    if (isDoubles) {
      // Для парных турниров: разрешаем сохранение даже если нет пар (для удаления всех участников)
      // Проверка не нужна, так как пользователь может удалить все пары
    }

    if (baseFee <= 0) {
      setError('Пожалуйста, введите корректную сумму взноса');
      return;
    }

    try {
      setIsLoading(true);
      
      const operations: Array<() => Promise<any>> = [];

      if (isDoubles) {
        // Для парных турниров: регистрируем пары
        // Получаем список уже зарегистрированных пар с сохранением исходного порядка
        const registeredPairs = tournament.memberships && Array.isArray(tournament.memberships)
          ? (() => {
              // Группируем членства по doublesTeamId
              const teamMap = new Map<string, string[]>();
              const doublesTeamsMap = new Map<string, any>();
              
              tournament.memberships
                .filter(m => isActiveMembershipForLevel(m, selectedLevel) && (m as any).doublesTeamId)
                .forEach(m => {
                  const teamId = (m as any).doublesTeamId;
                  if (!teamMap.has(teamId)) {
                    teamMap.set(teamId, []);
                  }
                  teamMap.get(teamId)!.push(m.playerId);
                  
                  // Сохраняем doublesTeam для получения исходного порядка
                  if ((m as any).doublesTeam && !doublesTeamsMap.has(teamId)) {
                    doublesTeamsMap.set(teamId, (m as any).doublesTeam);
                  }
                });
              
              // Преобразуем в массив пар с сохранением исходного порядка
              const pairs: Array<{player1Id: string; player2Id: string}> = [];
              teamMap.forEach((playerIds, teamId) => {
                if (playerIds.length === 2) {
                  const doublesTeam = doublesTeamsMap.get(teamId);
                  if (doublesTeam && doublesTeam.player1Id && doublesTeam.player2Id) {
                    // Используем порядок из doublesTeam
                    pairs.push({
                      player1Id: doublesTeam.player1Id,
                      player2Id: doublesTeam.player2Id
                    });
                  } else {
                    // Fallback: используем порядок из teamMap
                    pairs.push({
                      player1Id: playerIds[0],
                      player2Id: playerIds[1]
                    });
                  }
                } else if (playerIds.length === 1) {
                  // Неполная пара
                  const doublesTeam = doublesTeamsMap.get(teamId);
                  if (doublesTeam && doublesTeam.player1Id) {
                    pairs.push({
                      player1Id: doublesTeam.player1Id,
                      player2Id: doublesTeam.player2Id || ''
                    });
                  } else {
                    pairs.push({
                      player1Id: playerIds[0],
                      player2Id: ''
                    });
                  }
                }
              });
              return pairs;
            })()
          : [];

        // Вспомогательная функция для сравнения пар (с учетом нормализации для полных пар)
        const arePairsEqual = (pair1: {player1Id: string; player2Id: string}, pair2: {player1Id: string; player2Id: string}): boolean => {
          // Для неполных пар сравниваем только по player1Id
          if (!pair1.player2Id && !pair2.player2Id) {
            return pair1.player1Id === pair2.player1Id;
          }
          // Разные типы пар (полная vs неполная) - не равны
          if (!pair1.player2Id || !pair2.player2Id) {
            return false;
          }
          // Для полных пар используем нормализацию (порядок не важен)
          const normalized1 = {
            player1Id: pair1.player1Id < pair1.player2Id ? pair1.player1Id : pair1.player2Id,
            player2Id: pair1.player1Id < pair1.player2Id ? pair1.player2Id : pair1.player1Id
          };
          const normalized2 = {
            player1Id: pair2.player1Id < pair2.player2Id ? pair2.player1Id : pair2.player2Id,
            player2Id: pair2.player1Id < pair2.player2Id ? pair2.player2Id : pair2.player1Id
          };
          return normalized1.player1Id === normalized2.player1Id && normalized1.player2Id === normalized2.player2Id;
        };

        // Определяем, какие пары нужно добавить
        const toAdd = selectedPairs.filter(pair => {
          return !registeredPairs.some(rp => arePairsEqual(pair, rp));
        });

        // Определяем, какие пары нужно удалить
        const toRemove = registeredPairs.filter(rp => {
          return !selectedPairs.some(sp => arePairsEqual(rp, sp));
        });
        
        console.log(`[DEBUG] Всего операций для выполнения: toAdd=${toAdd.length}, toRemove=${toRemove.length}`);

        // Добавляем новые пары
        toAdd.forEach(pair => {
          if (!pair.player2Id) {
            // Неполная пара - регистрируем только первого игрока (без пары)
            operations.push(() => {
              console.log(`[DEBUG] Регистрация неполной пары: playerId=${pair.player1Id}, level=${selectedLevel}, fee=${baseFee}`);
              return apiPost(`/tournaments/${tournament.id}/players`, {
                playerId: pair.player1Id,
                level: selectedLevel,
                fee: baseFee
              });
            });
          } else {
            // Полная пара
            operations.push(() => {
              console.log(`[DEBUG] Регистрация полной пары: player1Id=${pair.player1Id}, player2Id=${pair.player2Id}, level=${selectedLevel}, fee=${baseFee}`);
              return apiPost(`/tournaments/${tournament.id}/players`, {
                playerId: pair.player1Id,
                player2Id: pair.player2Id,
                level: selectedLevel,
                fee: baseFee
              });
            });
          }
        });

        // Удаляем пары (удаляем через API - удаляем одного из игроков, что удалит всю команду)
        // ВАЖНО: используем player1Id из исходного порядка (не нормализованного)
        for (const pairToRemove of toRemove) {
          const playerIdToDelete = pairToRemove.player1Id; // Используем player1Id из исходного порядка
          console.log(`[DEBUG] Удаление пары: player1Id=${pairToRemove.player1Id}, player2Id=${pairToRemove.player2Id || 'нет'}, удаляем через playerId=${playerIdToDelete}`);
          operations.push(() => {
            console.log(`[DEBUG] API DELETE: /tournaments/${tournament.id}/players/${playerIdToDelete}?level=${selectedLevel}`);
            return apiDelete(`/tournaments/${tournament.id}/players/${playerIdToDelete}?level=${selectedLevel}`);
          });
        }
      } else {
        // Для одиночных турниров: регистрируем отдельных игроков
        // Получаем список уже зарегистрированных игроков на выбранном уровне
        const alreadyRegisteredIds = tournament.memberships && Array.isArray(tournament.memberships)
          ? players
              .filter(player => 
                tournament.memberships.some(
                  membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
                )
              )
              .map(player => player.id)
          : [];

        // Определяем, кого нужно добавить, а кого удалить
        const toAdd = selectedPlayerIds.filter(id => !alreadyRegisteredIds.includes(id)); // Новые игроки
        const toRemove = alreadyRegisteredIds.filter(id => !selectedPlayerIds.includes(id)); // Игроки, которых нужно удалить
        const existingWildCardByPlayer = new Map<string, boolean>();
        if (tournament.memberships && Array.isArray(tournament.memberships)) {
          tournament.memberships
            .filter(m => isActiveMembershipForLevel(m, selectedLevel))
            .forEach((m) => {
              if ((m as any).isWildCard === true) {
                existingWildCardByPlayer.set(m.playerId, true);
              } else if (!existingWildCardByPlayer.has(m.playerId)) {
                existingWildCardByPlayer.set(m.playerId, false);
              }
            });
        }
        
        // Добавляем регистрации для новых игроков
        toAdd.forEach(playerId => {
          operations.push(() => apiPost(`/tournaments/${tournament.id}/players`, {
            playerId,
            level: selectedLevel,
            fee: baseFee,
            isWildCard: wildCardPlayerIds.has(playerId)
          }));
        });
        
        // Для уже зарегистрированных игроков, которые выбраны, создаем дополнительные регистрации
        // Количество дополнительных регистраций берем из счетчика additionalRegistrations
        // ИСПОЛЬЗУЕМ REF для получения актуального значения, чтобы избежать проблем с замыканиями
        const alreadyRegisteredAndSelected = alreadyRegisteredIds.filter(id => selectedPlayerIds.includes(id));
        const currentAdditionalRegistrations = additionalRegistrationsRef.current;
        console.log('[DEBUG] Уже зарегистрированные и выбранные игроки:', alreadyRegisteredAndSelected);
        console.log('[DEBUG] Счетчики дополнительных регистраций (из state):', Array.from(additionalRegistrations.entries()));
        console.log('[DEBUG] Счетчики дополнительных регистраций (из ref):', Array.from(currentAdditionalRegistrations.entries()));
        
        alreadyRegisteredAndSelected.forEach(playerId => {
          const additionalCount = currentAdditionalRegistrations.get(playerId) || 0;
          console.log(`[DEBUG] Игрок ${playerId}: дополнительных регистраций = ${additionalCount}`);
          // Создаем столько дополнительных регистраций, сколько раз пользователь нажал "+"
          for (let i = 0; i < additionalCount; i++) {
            console.log(`[DEBUG] Создаем дополнительную регистрацию ${i + 1}/${additionalCount} для игрока ${playerId}`);
            operations.push(() => apiPost(`/tournaments/${tournament.id}/players`, {
              playerId,
              level: selectedLevel,
              fee: baseFee,
              isWildCard: wildCardPlayerIds.has(playerId)
            }));
          }
        });

        // Обновляем WC-флаг для уже зарегистрированных игроков выбранного уровня
        alreadyRegisteredAndSelected.forEach(playerId => {
          const currentWildCard = existingWildCardByPlayer.get(playerId) === true;
          const targetWildCard = wildCardPlayerIds.has(playerId);
          if (currentWildCard !== targetWildCard) {
            operations.push(() => apiPut(`/tournaments/${tournament.id}/players/${playerId}`, {
              level: selectedLevel,
              isWildCard: targetWildCard
            }));
          }
        });

        // Удаляем регистрации только в рамках выбранного уровня.
        // Без level backend удаляет игрока из всех уровней турнира.
        toRemove.forEach(playerId => {
          operations.push(() => apiDelete(`/tournaments/${tournament.id}/players/${playerId}?level=${selectedLevel}`));
        });
        
        console.log(`[DEBUG] После добавления всех операций: всего операций = ${operations.length}`);
        console.log(`[DEBUG] toAdd.length = ${toAdd.length}, toRemove.length = ${toRemove.length}, дополнительных регистраций = ${Array.from(currentAdditionalRegistrations.values()).reduce((sum, count) => sum + count, 0)}`);
      }

      // Выполняем запросы последовательно, чтобы избежать 429 (rate limit)
      const results: Array<{ ok: boolean; status?: number; statusText?: string; error?: string }> = [];
      console.log(`[DEBUG] Начинаем выполнение ${operations.length} операций`);
      for (let i = 0; i < operations.length; i++) {
        try {
          console.log(`[DEBUG] Выполнение операции ${i + 1}/${operations.length}`);
          const result = await operations[i]();
          console.log(`[DEBUG] Операция ${i + 1} успешно выполнена:`, result);
          results.push({ ok: true });
          // Небольшая пауза между запросами
          await new Promise(r => setTimeout(r, 120));
        } catch (e: any) {
          const errorMsg = e?.response?.status ? `HTTP ${e.response.status}: ${e.response.statusText || 'Unauthorized'}` : (e?.message || 'Request failed');
          console.error(`[DEBUG] Ошибка при выполнении операции ${i + 1}:`, errorMsg, e);
          results.push({ ok: false, error: errorMsg, status: e?.response?.status, statusText: e?.response?.statusText });
          await new Promise(r => setTimeout(r, 120));
        }
      }
      console.log(`[DEBUG] Все операции выполнены. Успешно: ${results.filter(r => r.ok).length}, Ошибок: ${results.filter(r => !r.ok).length}`);

      const successful = results.filter(r => r.ok).length;
      const failed = results.filter(r => !r.ok);

      if (successful > 0) {
        // Обновляем данные турнира, чтобы получить актуальные memberships с doublesTeamId
        try {
          const updatedTournament = await apiGet<any>(`/tournaments/${tournament.id}`);
          if (updatedTournament) {
            setTournament(updatedTournament);
            // Небольшая задержка, чтобы состояние обновилось
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (error) {
          console.error('Ошибка обновления данных турнира:', error);
        }
        
        // Вызываем onSuccess, который обновит данные турнира в родительском компоненте
        // Сбрасываем счетчики дополнительных регистраций после успешного сохранения
        setAdditionalRegistrations(new Map());
        await onSuccess();
        onClose();
        
        let message = '';
        if (isDoubles) {
          const toAdd = selectedPairs.filter(pair => {
            const registeredPairs = tournament.memberships && Array.isArray(tournament.memberships)
              ? (() => {
                  const teamMap = new Map<string, string[]>();
                  tournament.memberships
                    .filter(m => isActiveMembershipForLevel(m, selectedLevel) && (m as any).doublesTeamId)
                    .forEach(m => {
                      const teamId = (m as any).doublesTeamId;
                      if (!teamMap.has(teamId)) {
                        teamMap.set(teamId, []);
                      }
                      teamMap.get(teamId)!.push(m.playerId);
                    });
                  const pairs: Array<{player1Id: string; player2Id: string}> = [];
                  teamMap.forEach((playerIds) => {
                    if (playerIds.length === 2) {
                      pairs.push({
                        player1Id: playerIds[0] < playerIds[1] ? playerIds[0] : playerIds[1],
                        player2Id: playerIds[0] < playerIds[1] ? playerIds[1] : playerIds[0]
                      });
                    }
                  });
                  return pairs;
                })()
              : [];
            const normalizedPair = {
              player1Id: pair.player1Id < pair.player2Id ? pair.player1Id : pair.player2Id,
              player2Id: pair.player1Id < pair.player2Id ? pair.player2Id : pair.player1Id
            };
            return !registeredPairs.some(rp => 
              rp.player1Id === normalizedPair.player1Id && rp.player2Id === normalizedPair.player2Id
            );
          });
          if (toAdd.length > 0) message += `Добавлено: ${toAdd.length} пар. `;
        } else {
          const alreadyRegisteredIds = tournament.memberships && Array.isArray(tournament.memberships)
            ? players
                .filter(player => 
                  tournament.memberships.some(
                    membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
                  )
                )
                .map(player => player.id)
            : [];
          // ВАЖНО: Все выбранные игроки создают регистрации (включая уже зарегистрированных - для дополнительных регистраций)
          const toAdd = selectedPlayerIds;
          const toRemove = alreadyRegisteredIds.filter(id => !selectedPlayerIds.includes(id));
          if (toAdd.length > 0) message += `Добавлено регистраций: ${toAdd.length}. `;
          if (toRemove.length > 0) message += `Удалено: ${toRemove.length} игроков. `;
        }
        if (failed.length > 0) message += `Ошибок: ${failed.length}`;
        
        alert(message.trim() || 'Операции выполнены успешно');
      } else {
        // Показываем детали ошибок
        const errorMessages = failed.map(result => result.error ? result.error : `HTTP ${result.status}: ${result.statusText}`);
        
        setError(`Не удалось выполнить операции: ${errorMessages.join(', ')}`);
      }
    } catch (error) {
      console.error('Error managing players:', error);
      setError('Ошибка управления игроками');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          Управление участниками турнира
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {tournament.name}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Выбор уровня */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Уровень участия *
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setShowAllLowerLevels(false);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {levels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          {/* Поиск игроков + базовая величина взносов */}
          {selectedLevel && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Поиск игроков
                </label>
                <input
                  type="text"
                  value={playerSearch}
                  onChange={(e) => setPlayerSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Например: alex di"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Поиск по началу любых слов в имени
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Базовая величина взносов для уровня {selectedLevel} (MDL) *
                </label>
                <input
                  type="number"
                  value={baseFee}
                  onChange={(e) => setBaseFee(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите сумму взноса"
                  min="0"
                  step="1"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Эта сумма будет использоваться для всех игроков данного уровня
                </p>
              </div>
            </div>
          )}

          {/* Список игроков или выбор пар */}
          {selectedLevel && (
            <div>
              {isDoubles ? (
                // Интерфейс для парных турниров: выбор пар
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Пары игроков уровня {selectedLevel}
                    </h3>
                    <span className="text-sm text-gray-600">
                      Выбрано пар: {selectedPairs.length}
                    </span>
                  </div>

                  {/* Форма добавления пары */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Игрок 1 *
                        </label>
                        <select
                          value={pairPlayer1Id}
                          onChange={(e) => setPairPlayer1Id(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Выберите игрока</option>
                          {filteredPlayers
                            .filter(p => !pairPlayer2Id || p.id !== pairPlayer2Id)
                            .map(player => (
                              <option key={player.id} value={player.id}>
                                {player.name} {player.doublesLevel && `(${player.doublesLevel})`}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Игрок 2 *
                        </label>
                        <select
                          value={pairPlayer2Id}
                          onChange={(e) => setPairPlayer2Id(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Выберите игрока</option>
                          {(() => {
                            let availablePlayers = filteredPlayers.filter(p => !pairPlayer1Id || p.id !== pairPlayer1Id);
                            
                            // ВРЕМЕННО ОТКЛЮЧЕНО: Для уровня D-ProAm применяем специальную фильтрацию
                            /* Закомментировано временно
                            if (selectedLevel === 'D-ProAm' && pairPlayer1Id) {
                              const player1 = players.find(p => p.id === pairPlayer1Id);
                              if (player1 && player1.doublesLevel === 'D-ProAm') {
                                // Если первый игрок D-ProAm, второй может быть любого уровня КРОМЕ D-ProAm
                                availablePlayers = availablePlayers.filter(p => {
                                  const level = p.doublesLevel;
                                  if (!level) return false;
                                  return level !== 'D-ProAm'; // Исключаем только D-ProAm
                                });
                              }
                              // Если первый игрок НЕ D-ProAm, второй может быть любого уровня (включая D-ProAm)
                              // Никаких дополнительных ограничений не требуется
                            }
                            */
                            
                            return availablePlayers.map(player => (
                              <option key={player.id} value={player.id}>
                                {player.name} {player.doublesLevel && `(${player.doublesLevel})`}
                              </option>
                            ));
                          })()}
                        </select>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddPair}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Добавить пару
                    </button>
                  </div>

                  {/* Список выбранных пар */}
                  {selectedPairs.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Выбранные пары:</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {selectedPairs.map((pair, index) => {
                          const player1 = players.find(p => p.id === pair.player1Id);
                          const player2 = pair.player2Id ? players.find(p => p.id === pair.player2Id) : null;
                          const isIncomplete = !pair.player2Id;
                          return (
                            <div
                              key={index}
                              className={`flex items-center justify-between p-2 border rounded ${
                                isIncomplete 
                                  ? 'bg-yellow-50 border-yellow-200' 
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <span className="text-sm">
                                {player1?.name || pair.player1Id} 
                                {isIncomplete ? (
                                  <span className="text-yellow-600 italic"> / (нет партнера)</span>
                                ) : (
                                  <> / {player2?.name || pair.player2Id}</>
                                )}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemovePair(index)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Удалить
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Список игроков без пары (для удаления) */}
                  {registeredSoloPlayers.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Игроки без пары (для удаления):
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-yellow-300 rounded-lg p-2 bg-yellow-50">
                        {registeredSoloPlayers.map((playerId) => {
                          const player = players.find(p => p.id === playerId);
                          return (
                            <div
                              key={playerId}
                              className="flex items-center justify-between p-2 bg-white border border-yellow-200 rounded"
                            >
                              <span className="text-sm">
                                {player?.name || playerId}
                              </span>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await apiDelete(`/tournaments/${tournament.id}/players/${playerId}?level=${selectedLevel}`);
                                    // Обновляем список после удаления
                                    setRegisteredSoloPlayers(prev => prev.filter(id => id !== playerId));
                                    // Обновляем данные турнира
                                    const updatedTournament = await apiGet<any>(`/tournaments/${tournament.id}`);
                                    if (updatedTournament) {
                                      setTournament(updatedTournament);
                                    }
                                    // Сбрасываем счетчики дополнительных регистраций после успешного сохранения
        setAdditionalRegistrations(new Map());
        await onSuccess();
                                  } catch (error) {
                                    console.error('Ошибка удаления игрока:', error);
                                    setError('Не удалось удалить игрока');
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Удалить
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Интерфейс для одиночных турниров: выбор отдельных игроков
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Игроки уровня {selectedLevel}{showAllLowerLevels && selectedLevel.startsWith('S-') ? ' и ниже' : ''}
                    </h3>
                <div className="flex items-center space-x-4">
                  {selectedLevel.startsWith('S-') && (
                    <label className="flex items-center space-x-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={showAllLowerLevels}
                        onChange={(e) => setShowAllLowerLevels(e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span>Все уровни</span>
                    </label>
                  )}
                  <span className="text-sm text-gray-600">
                    {(() => {
                      // Подсчитываем общее количество игроков с выбранным уровнем
                      const totalPlayersWithLevel = players.filter(player => 
                        isVisibleForSelectedLevel(player, selectedLevel)
                      ).length;
                      
                      // Подсчитываем количество уникальных игроков, которые уже зарегистрированы (хотя бы один раз)
                      const alreadyRegisteredCount = tournament.memberships && Array.isArray(tournament.memberships)
                        ? players.filter(player => 
                            isVisibleForSelectedLevel(player, selectedLevel) &&
                            tournament.memberships.some(
                              membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
                            )
                          ).length
                        : 0;
                      
                      // Подсчитываем общее количество регистраций (может быть больше, чем количество игроков)
                      const totalRegistrationsCount = tournament.memberships && Array.isArray(tournament.memberships)
                        ? tournament.memberships.filter(
                            membership => isActiveMembershipForLevel(membership, selectedLevel) &&
                            players.some(player => 
                              player.id === membership.playerId && 
                              isVisibleForSelectedLevel(player, selectedLevel)
                            )
                          ).length
                        : 0;
                      
                      return (
                        <>
                          Выбрано: {selectedPlayerIds.length} из {totalPlayersWithLevel}
                          {alreadyRegisteredCount > 0 && (
                            <span className="text-blue-600 ml-2">
                              (уже зарегистрированы: {alreadyRegisteredCount} {totalRegistrationsCount > alreadyRegisteredCount ? `(${totalRegistrationsCount} регистраций)` : ''})
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </span>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {(() => {
                      const allPlayersWithLevel = players.filter(player => 
                        isVisibleForSelectedLevel(player, selectedLevel)
                      );
                      return selectedPlayerIds.length === allPlayersWithLevel.length ? 'Снять все' : 'Выбрать все';
                    })()}
                  </button>
                </div>
              </div>
              
              {/* Показываем информацию о уже зарегистрированных игроках */}
              {(() => {
                const alreadyRegisteredCount = tournament.memberships && Array.isArray(tournament.memberships)
                  ? players.filter(player => 
                      isVisibleForSelectedLevel(player, selectedLevel) &&
                      tournament.memberships.some(
                        membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
                      )
                    ).length
                  : 0;
                
                const totalRegistrationsCount = tournament.memberships && Array.isArray(tournament.memberships)
                  ? tournament.memberships.filter(
                      membership => isActiveMembershipForLevel(membership, selectedLevel) &&
                      players.some(player => 
                        player.id === membership.playerId && 
                        isVisibleForSelectedLevel(player, selectedLevel)
                      )
                    ).length
                  : 0;
                
                return alreadyRegisteredCount > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">{alreadyRegisteredCount}</span> игроков уже зарегистрированы на этот турнир с уровнем {selectedLevel}
                      {totalRegistrationsCount > alreadyRegisteredCount && (
                        <span> (всего {totalRegistrationsCount} регистраций - некоторые игроки зарегистрированы несколько раз)</span>
                      )}
                      . Используйте кнопку "+" для добавления дополнительных регистраций.
                    </p>
                  </div>
                );
              })()}

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredPlayers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {(() => {
                      const totalPlayersWithLevel = players.filter(player => 
                        player.singlesLevel === selectedLevel || player.doublesLevel === selectedLevel
                      ).length;
                      
                      if (totalPlayersWithLevel === 0) {
                        return `Нет игроков с уровнем ${selectedLevel}`;
                      } else {
                        return `Все игроки с уровнем ${selectedLevel} уже зарегистрированы на этот турнир`;
                      }
                    })()}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredPlayers.map(player => {
                      // Подсчитываем количество регистраций для этого игрока на этом уровне
                      const registrationCount = tournament.memberships && Array.isArray(tournament.memberships)
                        ? tournament.memberships.filter(
                            membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
                          ).length
                        : 0;
                      
                      const isAlreadyRegistered = registrationCount > 0;
                      const isWildCardSelected = wildCardPlayerIds.has(player.id);
                      
                      return (
                        <label
                          key={player.id}
                          className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-all ${
                            isAlreadyRegistered ? 'bg-blue-50 border-l-4 border-blue-400' : ''
                          } ${
                            recentlyAddedIds.has(player.id) ? 'bg-green-100 border-l-4 border-green-500' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPlayerIds.includes(player.id)}
                            onChange={() => handlePlayerToggle(player.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {player.name}
                                </span>
                                {isAlreadyRegistered && (
                                  <div className="flex items-center space-x-1">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {registrationCount === 1 
                                        ? 'Зарегистрирован' 
                                        : `Зарегистрирован ${registrationCount} раза`}
                                    </span>
                                    {(additionalRegistrations.get(player.id) > 0 || registrationCount > 1) && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {additionalRegistrations.get(player.id) > 0 
                                          ? `+${additionalRegistrations.get(player.id)} доп.` 
                                          : registrationCount > 1 
                                            ? `(${registrationCount - 1} доп. в базе)`
                                            : ''}
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        // Кнопка "+" увеличивает счетчик дополнительных регистраций для этого игрока
                                        setAdditionalRegistrations(prev => {
                                          const newMap = new Map(prev);
                                          const currentCount = newMap.get(player.id) || 0;
                                          const newCount = currentCount + 1;
                                          newMap.set(player.id, newCount);
                                          console.log(`[DEBUG] Увеличиваем счетчик для игрока ${player.name} (${player.id}): ${currentCount} -> ${newCount}`);
                                          console.log(`[DEBUG] Текущее состояние счетчиков:`, Array.from(newMap.entries()));
                                          return newMap;
                                        });
                                        
                                        // Визуальная обратная связь
                                        setRecentlyAddedIds(prevIds => new Set([...prevIds, player.id]));
                                        setTimeout(() => {
                                          setRecentlyAddedIds(prevIds => {
                                            const newSet = new Set(prevIds);
                                            newSet.delete(player.id);
                                            return newSet;
                                          });
                                        }, 500);
                                        
                                        // Убеждаемся, что игрок выбран
                                        setSelectedPlayerIds(prev => {
                                          if (!prev.includes(player.id)) {
                                            return [...prev, player.id];
                                          }
                                          return prev;
                                        });
                                      }}
                                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 text-xs font-bold transition-all active:scale-90 ${
                                        recentlyAddedIds.has(player.id) 
                                          ? 'bg-green-700 scale-110 animate-pulse' 
                                          : 'bg-green-500'
                                      }`}
                                      title="Добавить еще одну регистрацию (нажмите для создания дополнительной регистрации)"
                                    >
                                      +
                                    </button>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleWildCardToggle(player.id);
                                    setSelectedPlayerIds(prev => (
                                      prev.includes(player.id) ? prev : [...prev, player.id]
                                    ));
                                  }}
                                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                                    isWildCardSelected
                                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                  title="Отметить регистрацию игрока как Wild Card"
                                >
                                  WC
                                </button>
                              </div>
                              <div className="flex space-x-2">
                                {player.singlesLevel && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {player.singlesLevel}
                                  </span>
                                )}
                                {player.doublesLevel && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {player.doublesLevel}
                                  </span>
                                )}
                              </div>
                            </div>
                            {player.email && (
                              <p className="text-xs text-gray-500">{player.email}</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isLoading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={(() => {
                if (isLoading) return true;
                if (!selectedLevel) return true;
                if (isDoubles) {
                  // Проверяем, есть ли изменения (разрешаем сохранение даже если список пуст - для удаления всех пар)
                  // Используем doublesTeam для получения правильного порядка из базы
                  const registeredPairsWithOrder = tournament.memberships && Array.isArray(tournament.memberships)
                    ? (() => {
                        const pairsMap = new Map<string, {player1Id: string; player2Id: string}>();
                        const teamMap = new Map<string, string[]>();
                        tournament.memberships
                          .filter(m => isActiveMembershipForLevel(m, selectedLevel) && m.doublesTeamId)
                          .forEach(m => {
                            const teamId = m.doublesTeamId!;
                            if (!teamMap.has(teamId)) {
                              teamMap.set(teamId, []);
                            }
                            teamMap.get(teamId)!.push(m.playerId);
                            
                            // Используем doublesTeam для получения правильного порядка
                            const doublesTeam = (m as any).doublesTeam;
                            if (doublesTeam && doublesTeam.id && !pairsMap.has(teamId)) {
                              pairsMap.set(teamId, {
                                player1Id: doublesTeam.player1Id,
                                player2Id: doublesTeam.player2Id || ''
                              });
                            }
                          });
                        
                        // Заполняем пары из teamMap, если doublesTeam не загружен
                        teamMap.forEach((playerIds, teamId) => {
                          if (!pairsMap.has(teamId)) {
                            if (playerIds.length === 2) {
                              pairsMap.set(teamId, {
                                player1Id: playerIds[0],
                                player2Id: playerIds[1]
                              });
                            } else if (playerIds.length === 1) {
                              pairsMap.set(teamId, {
                                player1Id: playerIds[0],
                                player2Id: ''
                              });
                            }
                          }
                        });
                        
                        return Array.from(pairsMap.values());
                      })()
                    : [];
                  
                  // Сравниваем пары с учетом порядка и неполных пар
                  const toAdd = selectedPairs.filter(sp => {
                    return !registeredPairsWithOrder.some(rp => {
                      // Для неполных пар сравниваем только по player1Id
                      if (!sp.player2Id && !rp.player2Id) {
                        return sp.player1Id === rp.player1Id;
                      }
                      // Для полных пар сравниваем с учетом порядка
                      if (sp.player2Id && rp.player2Id) {
                        return sp.player1Id === rp.player1Id && sp.player2Id === rp.player2Id;
                      }
                      return false; // Разные типы пар (полная vs неполная)
                    });
                  });
                  
                  const toRemove = registeredPairsWithOrder.filter(rp => {
                    return !selectedPairs.some(sp => {
                      // Для неполных пар сравниваем только по player1Id
                      if (!sp.player2Id && !rp.player2Id) {
                        return sp.player1Id === rp.player1Id;
                      }
                      // Для полных пар сравниваем с учетом порядка
                      if (sp.player2Id && rp.player2Id) {
                        return sp.player1Id === rp.player1Id && sp.player2Id === rp.player2Id;
                      }
                      return false; // Разные типы пар (полная vs неполная)
                    });
                  });
                  
                  // Проверяем изменение базовой величины взносов
                  const firstMembership = tournament.memberships?.find(
                    m => isActiveMembershipForLevel(m, selectedLevel)
                  );
                  const feeChanged = firstMembership ? (firstMembership.fee || 0) !== baseFee : baseFee > 0;
                  
                  const hasChanges = toAdd.length > 0 || toRemove.length > 0 || feeChanged;
                  console.log(`[DEBUG] Проверка disabled кнопки (DOUBLES): toAdd=${toAdd.length}, toRemove=${toRemove.length}, feeChanged=${feeChanged}, hasChanges=${hasChanges}, selectedPairs=${selectedPairs.length}, registeredPairs=${registeredPairsWithOrder.length}`);
                  return !hasChanges;
                } else {
                  // Проверяем, есть ли изменения
                  const alreadyRegisteredIds = tournament.memberships && Array.isArray(tournament.memberships)
                    ? players
                        .filter(player => 
                          tournament.memberships.some(
                            membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
                          )
                        )
                        .map(player => player.id)
                    : [];
                  
                  // ВАЖНО: Все выбранные игроки создают регистрации (включая уже зарегистрированных - для дополнительных регистраций)
                  const toAdd = selectedPlayerIds;
                  const toRemove = alreadyRegisteredIds.filter(id => !selectedPlayerIds.includes(id));
                  
                  // Проверяем изменение базовой величины взносов
                  const firstMembership = tournament.memberships?.find(
                    m => isActiveMembershipForLevel(m, selectedLevel)
                  );
                  const feeChanged = firstMembership ? (firstMembership.fee || 0) !== baseFee : baseFee > 0;
                  const existingWildCardByPlayer = new Map<string, boolean>();
                  if (tournament.memberships && Array.isArray(tournament.memberships)) {
                    tournament.memberships
                      .filter(m => isActiveMembershipForLevel(m, selectedLevel))
                      .forEach((m) => {
                        if ((m as any).isWildCard === true) {
                          existingWildCardByPlayer.set(m.playerId, true);
                        } else if (!existingWildCardByPlayer.has(m.playerId)) {
                          existingWildCardByPlayer.set(m.playerId, false);
                        }
                      });
                  }
                  const wildCardChanged = selectedPlayerIds.some((playerId) => {
                    if (!alreadyRegisteredIds.includes(playerId)) return false;
                    return (existingWildCardByPlayer.get(playerId) === true) !== wildCardPlayerIds.has(playerId);
                  });
                  
                  const hasChanges = toAdd.length > 0 || toRemove.length > 0 || feeChanged || wildCardChanged;
                  return !hasChanges;
                }
              })()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(() => {
                if (isLoading) {
                  return 'Сохранение...';
                }
                
                if (isDoubles) {
                  // Для парных турниров: вычисляем количество изменений
                  const registeredPairs = tournament.memberships && Array.isArray(tournament.memberships)
                    ? (() => {
                        const teamMap = new Map<string, string[]>();
                        tournament.memberships
                          .filter(m => isActiveMembershipForLevel(m, selectedLevel) && (m as any).doublesTeamId)
                          .forEach(m => {
                            const teamId = (m as any).doublesTeamId;
                            if (!teamMap.has(teamId)) {
                              teamMap.set(teamId, []);
                            }
                            teamMap.get(teamId)!.push(m.playerId);
                          });
                        const pairs: Array<{player1Id: string; player2Id: string}> = [];
                        teamMap.forEach((playerIds) => {
                          if (playerIds.length === 2) {
                            pairs.push({
                              player1Id: playerIds[0] < playerIds[1] ? playerIds[0] : playerIds[1],
                              player2Id: playerIds[0] < playerIds[1] ? playerIds[1] : playerIds[0]
                            });
                          }
                        });
                        return pairs;
                      })()
                    : [];
                  
                  const toAdd = selectedPairs.filter(pair => {
                    const normalizedPair = {
                      player1Id: pair.player1Id < pair.player2Id ? pair.player1Id : pair.player2Id,
                      player2Id: pair.player1Id < pair.player2Id ? pair.player2Id : pair.player1Id
                    };
                    return !registeredPairs.some(rp => 
                      rp.player1Id === normalizedPair.player1Id && rp.player2Id === normalizedPair.player2Id
                    );
                  });
                  
                  const toRemove = registeredPairs.filter(rp => {
                    const normalizedPair = {
                      player1Id: rp.player1Id < rp.player2Id ? rp.player1Id : rp.player2Id,
                      player2Id: rp.player1Id < rp.player2Id ? rp.player2Id : rp.player1Id
                    };
                    return !selectedPairs.some(sp => {
                      const normalizedSelected = {
                        player1Id: sp.player1Id < sp.player2Id ? sp.player1Id : sp.player2Id,
                        player2Id: sp.player1Id < sp.player2Id ? sp.player2Id : sp.player1Id
                      };
                      return normalizedSelected.player1Id === normalizedPair.player1Id && 
                             normalizedSelected.player2Id === normalizedPair.player2Id;
                    });
                  });
                  
                  // Проверяем изменение базовой величины взносов
                  const firstMembership = tournament.memberships?.find(
                    m => isActiveMembershipForLevel(m, selectedLevel)
                  );
                  const feeChanged = firstMembership ? (firstMembership.fee || 0) !== baseFee : baseFee > 0;
                  
                  const changesCount = toAdd.length + toRemove.length + (feeChanged ? 1 : 0);
                  return `Сохранить изменения${changesCount > 0 ? ` (${changesCount})` : ''}`;
                } else {
                  // Для одиночных турниров: вычисляем количество изменений
                  const alreadyRegisteredIds = tournament.memberships && Array.isArray(tournament.memberships)
                    ? players
                        .filter(player => 
                          tournament.memberships.some(
                            membership => membership.playerId === player.id && isActiveMembershipForLevel(membership, selectedLevel)
                          )
                        )
                        .map(player => player.id)
                    : [];
                  
                  // ВАЖНО: Все выбранные игроки создают регистрации (включая уже зарегистрированных - для дополнительных регистраций)
                  const toAdd = selectedPlayerIds;
                  const toRemove = alreadyRegisteredIds.filter(id => !selectedPlayerIds.includes(id));
                  
                  // Проверяем изменение базовой величины взносов
                  const firstMembership = tournament.memberships?.find(
                    m => isActiveMembershipForLevel(m, selectedLevel)
                  );
                  const feeChanged = firstMembership ? (firstMembership.fee || 0) !== baseFee : baseFee > 0;
                  const existingWildCardByPlayer = new Map<string, boolean>();
                  if (tournament.memberships && Array.isArray(tournament.memberships)) {
                    tournament.memberships
                      .filter(m => isActiveMembershipForLevel(m, selectedLevel))
                      .forEach((m) => {
                        if ((m as any).isWildCard === true) {
                          existingWildCardByPlayer.set(m.playerId, true);
                        } else if (!existingWildCardByPlayer.has(m.playerId)) {
                          existingWildCardByPlayer.set(m.playerId, false);
                        }
                      });
                  }
                  const wildCardChanged = selectedPlayerIds.filter((playerId) => alreadyRegisteredIds.includes(playerId))
                    .filter((playerId) => (existingWildCardByPlayer.get(playerId) === true) !== wildCardPlayerIds.has(playerId))
                    .length;
                  
                  const changesCount = toAdd.length + toRemove.length + (feeChanged ? 1 : 0) + wildCardChanged;
                  return `Сохранить изменения${changesCount > 0 ? ` (${changesCount})` : ''}`;
                }
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkPlayerRegistrationForm;
